import { useContext, useEffect, useRef, useState } from "react";
import type { ChangeEvent, ClipboardEvent, FormEvent, KeyboardEvent } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import _ from "lodash";
import InputBase from "@material-ui/core/InputBase";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import { AuthContext } from "../../context/auth-context";
import roles, { roleBasedRoutes } from "../../constants/roles";
import { getUserBrowserAndDeviceInfo } from "../../utils/BrowserDetails";
import { fetchStepAuth, verifyOtp, fetchUserDetailsByEmail } from "./Service";
import Snackbar from "../../components/Snackbar";
import AuthPageBackground from "../../components/AuthPageBackground";
import { useGlobalStyles } from "../../components/globalStyles";
import { authenticatorStrings, commonStrings } from "../../components/StringBundle";
import logoPng from "../../assets/images/logo_pdf.png";
import type {
  SnackState,
  StepAuthResponse,
  VerifyOtpPayload,
  VerifyOtpResponse,
  UserDetailsByEmail,
  AuthContextValue,
  NavigationState,
  Coordinates,
} from "./types";

const OTP_LENGTH = 6;

// Two-step verification screen: shows a QR code for first-time setup, then verifies the 6-digit code.
export default function Authenticator() {
  const classes = useGlobalStyles() as Record<string, string>;
  const auth = useContext(AuthContext) as AuthContextValue;
  const location = useLocation();
  const navigate = useNavigate();
  const navigationState = location.state as NavigationState | null;

  const pendingToken = navigationState?.pendingToken || null;
  const isFirstTimeSetup = !navigationState?.mfaEnrolled;

  const [qrCodeText, setQrCodeText] = useState<string>("");
  const [secretCode, setSecretCode] = useState<string>("");
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [coords, setCoords] = useState<Coordinates>({
    latitude: null,
    longitude: null,
  });
  const [showSnack, setSnack] = useState<SnackState>({ open: false, message: "", severity: "" });
  const [loading, setLoading] = useState<boolean>(false);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const isOtpComplete = otp.every((digit) => digit !== "");

  // Fetches the QR code and secret key needed for first-time authenticator setup.
  useEffect(() => {
    if (!isFirstTimeSetup || !pendingToken) return;
    (async () => {
      try {
        const response: StepAuthResponse = await fetchStepAuth(pendingToken);
        setQrCodeText(response?.qrCodeURL || "");
        setSecretCode(response?.secret_code || "");
      } catch {
        // no-op — user can still enter a code from an already-configured authenticator app
      }
    })();
  }, [pendingToken, isFirstTimeSetup]);

  // Sends the user back to login if they land here without the required data.
  useEffect(() => {
    if (!pendingToken) {
      navigate("/login", { replace: true });
    }
  }, [pendingToken, navigate]);

  // Fetches the user's location once when the page loads.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => setCoords({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
      () => {
        // location is optional metadata — silently ignore if denied/unavailable
      }
    );
  }, []);

  // Moves focus to a specific OTP box.
  const focusInput = (index: number) => {
    inputRefs.current[index]?.focus();
  };

  // Sets a single OTP box's digit.
  const setOtpDigit = (index: number, value: string) => {
    setOtp((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  // Updates a single OTP digit and moves focus to the next box.
  const handleOtpChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const digit = e.target.value;
    if (!/^\d?$/.test(digit)) return;
    setOtpDigit(index, digit);
    if (digit && index < OTP_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  // Clears the current box on backspace, or moves back to the previous box if it's already empty.
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key !== "Backspace") return;
    if (otp[index] !== "") {
      setOtpDigit(index, "");
    } else if (index > 0) {
      focusInput(index - 1);
    }
  };

  // Fills all OTP boxes at once when a 6-digit code is pasted.
  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    if (!/^\d{6}$/.test(pasted)) return;
    setOtp(pasted.split(""));
    focusInput(OTP_LENGTH - 1);
  };

  // Saves the session details from a successful verify response. The real
  // session tokens arrive as HttpOnly cookies, not in this response — auth.storeToken
  // only has plain identity fields (role/user id/name/email) to persist.
  const persistSession = (response: VerifyOtpResponse) => {
    auth.storeToken(response);

    const preference = {
      name: navigationState?.preferenceName,
      lastPasswordDate: response.last_password_modified_date,
    };
    localStorage.setItem("preference", JSON.stringify(preference));
  };

  // Looks up and stores the caller's division — only relevant for RD roles, whose home screens filter by it.
  const maybeStoreDivision = async (role: string, email: string): Promise<void> => {
    if (role !== roles.rdManager && role !== roles.RDManagerBD && role !== roles.rdAssociate) return;
    const userDetails: UserDetailsByEmail = await fetchUserDetailsByEmail(email);
    if (userDetails) {
      sessionStorage.setItem("division", userDetails.division || "");
    }
  };

  // Sends the user back where they came from if their role allows it, otherwise their role's default page.
  const resolveRedirectPath = (role: string): string => {
    const fromPath = navigationState?.from?.pathname;
    const pathIndex = roleBasedRoutes?.[role]?.findIndex((path: string) => path === fromPath);
    if (pathIndex !== undefined && pathIndex > -1) {
      return fromPath || "/home";
    }
    return roleBasedRoutes?.[role]?.[0] || "/home";
  };

  // Verifies the OTP, logs the user in, and redirects based on their role.
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isOtpComplete) {
      setSnack({ open: true, message: authenticatorStrings.otpError, severity: commonStrings.error });
      return;
    }
    if (!pendingToken) return;

    const browserInfo = getUserBrowserAndDeviceInfo();
    const payload: VerifyOtpPayload = {
      browser_info: browserInfo?.browser,
      code: otp.join(""),
      device: browserInfo?.device,
      location_info: `${coords.latitude},${coords.longitude}`,
      pending_token: pendingToken,
    };
    if (!_.isEmpty(secretCode)) {
      payload.secret_code = secretCode;
    }

    setLoading(true);
    try {
      const response: VerifyOtpResponse = await verifyOtp(payload);
      if (!response) return;

      persistSession(response);

      // role/email_id arrive as plain fields now — no JWT wrapping to decode.
      const role = response.role;
      const email = response.email_id;

      await maybeStoreDivision(role, email);

      navigate(resolveRedirectPath(role));
    } catch (e) {
      const serverData = axios.isAxiosError(e)
        ? (e.response?.data as { message?: string; error?: string } | undefined)
        : undefined;

      // A dead/expired pending_token means there's no login left to retry — send the user back to login.
      // Flag set before navigating so Login.tsx (a fresh mount) can still show why they landed there.
      if (serverData?.error === authenticatorStrings.sessionExpired) {
        sessionStorage.setItem(authenticatorStrings.sessionExpiredStorageKey, "1");
        navigate("/login", { replace: true });
        return;
      }

      const message = serverData?.message === "Invalid OTP" ? authenticatorStrings.invalidOtp : authenticatorStrings.errormsg;
      setSnack({ open: true, message, severity: "error" });
      setTimeout(() => setLoading(false), 2000);
    }
  };

  const logoClass = `${classes.loginLogo}${isFirstTimeSetup ? ` ${classes.loginLogoTwostepCompact}` : ""}`;
  const headingClass = `${classes.authHeading}${isFirstTimeSetup ? ` ${classes.authHeadingTwostepCompact}` : ""}`;
  const subtitleClass = `${classes.loginSubtitle}${isFirstTimeSetup ? ` ${classes.loginSubtitleTwostepCompact}` : ""}`;

  return (
    <div className={classes.loginPage}>
      <AuthPageBackground />
      <div className={classes.loginCard}>
        <div className={classes.loginImagePanel} aria-hidden="true" />

        <div className={classes.loginFormPanel}>
          {showSnack.open && (
            <Snackbar
              severity={showSnack.severity}
              message={showSnack.message}
              handleClose={() => setSnack({ open: false, message: "", severity: "" })}
            />
          )}

          <img src={logoPng} alt={commonStrings.logoAlt} className={logoClass} />
          <Typography variant="h1" className={headingClass}>{authenticatorStrings.headerMsg}</Typography>

          {isFirstTimeSetup && (
            <>
              <Typography className={subtitleClass}>{authenticatorStrings.scan}</Typography>
              <div className={classes.twostepQr}>
                <QRCodeSVG value={qrCodeText} size={112} />
              </div>
            </>
          )}

          <Typography className={subtitleClass}>{authenticatorStrings.headertwo}</Typography>

          <form className={classes.loginForm} onSubmit={handleSubmit} noValidate>
            <div className={classes.twostepOtpRow}>
              {otp.map((digit, index) => (
                <InputBase
                  key={index}
                  inputRef={(el: HTMLInputElement | null) => {
                    inputRefs.current[index] = el;
                  }}
                  inputProps={{ className: classes.twostepOtpBox, maxLength: 1 }}
                  value={digit}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus={index === 0}
                  onChange={(e) => handleOtpChange(e as ChangeEvent<HTMLInputElement>, index)}
                  onKeyDown={(e) => handleKeyDown(e as KeyboardEvent<HTMLInputElement>, index)}
                  onPaste={handlePaste}
                />
              ))}
            </div>

            <Button type="submit" className={classes.loginSubmit} disabled={!isOtpComplete || loading} disableRipple>
              {loading ? commonStrings.loading : authenticatorStrings.authenticate}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
